/**
 * ============================================================
 * 登录/注册页面 (Login) - v9
 * ============================================================
 * 注册支持企业/个人两种类型
 *   企业必填：企业名称、对公账户、开户行名称、联系人姓名、电话
 *   个人必填：姓名、银行卡号、开户行、手机号码、身份证号
 * ============================================================
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth';
import { authApi } from '../api';
import {
  LogIn, UserPlus, User, Lock, Radio, Building2,
  CreditCard, Landmark, Phone, IdCard, UserCheck
} from 'lucide-react';

type LoginMode = 'login' | 'register';
type CardType = '个人' | '企业';

/** 注册表单 */
interface RegisterForm {
  username: string;
  password: string;
  confirmPassword: string;
  cardType: CardType;
  // 企业字段
  companyName: string;
  bankAccountPublic: string;
  bankName: string;
  contactName: string;
  contactPhone: string;
  // 个人字段
  realName: string;
  bankCard: string;
  personalBankName: string;
  mobilePhone: string;
  idCard: string;
}

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();

  const [mode, setMode] = useState<LoginMode>('login');
  const [loginForm, setLoginForm] = useState({ username: '', password: '' });
  const [registerForm, setRegisterForm] = useState<RegisterForm>({
    username: '', password: '', confirmPassword: '',
    cardType: '个人', companyName: '', bankAccountPublic: '',
    bankName: '', contactName: '', contactPhone: '',
    realName: '', bankCard: '', personalBankName: '',
    mobilePhone: '', idCard: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!loginForm.username.trim() || !loginForm.password.trim()) {
      setError('请输入用户名和密码');
      return;
    }
    setError('');
    setLoading(true);
    try {
      // 直接调用 authApi.login 获取用户信息
      const result = await authApi.login(loginForm.username, loginForm.password);
      // 设置 token
      if (result.token) {
        localStorage.setItem("token", result.token);
      }
      // 根据用户角色跳转到不同页面
      const originalRole = result.user?.role || '';
      const role = originalRole.toLowerCase();
      console.log('[Login] 用户角色:', originalRole, '转换后:', role);
      // 兼容中英文角色名
      if (role === 'finance' || originalRole === '财务') {
        console.log('[Login] 财务角色，跳转到工资提报页面');
        setTimeout(() => {
          window.location.href = '/wage-declaration';
        }, 100);
      } else {
        // 管理员和经纪人跳转账单管理页面
        console.log('[Login] 非财务角色(' + originalRole + ')，跳转账单管理页面');
        setTimeout(() => {
          window.location.href = '/bills';
        }, 100);
      }
    } catch (err: any) {
      setError(err.message || '登录失败');
    } finally {
      setLoading(false);
    }
  };

  /** 验证注册表单 */
  const validateRegister = (): string | null => {
    const f = registerForm;
    if (!f.username.trim()) return '请输入登录账号';
    if (!f.password.trim()) return '请输入登录密码';
    if (f.password !== f.confirmPassword) return '两次输入的密码不一致';

    if (f.cardType === '企业') {
      if (!f.companyName.trim()) return '企业名称为必填项';
      if (!f.bankAccountPublic.trim()) return '对公账户为必填项';
      if (!f.bankName.trim()) return '开户行名称为必填项';
      if (!f.contactName.trim()) return '联系人姓名为必填项';
      if (!f.contactPhone.trim()) return '联系电话为必填项';
    } else {
      if (!f.realName.trim()) return '姓名为必填项';
      if (!f.bankCard.trim()) return '银行卡号为必填项';
      if (!f.personalBankName.trim()) return '开户行为必填项';
      if (!f.mobilePhone.trim()) return '手机号码为必填项';
      if (!f.idCard.trim()) return '身份证号为必填项';
    }
    return null;
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    const err = validateRegister();
    if (err) { setError(err); return; }
    setError('');
    setLoading(true);
    try {
      const f = registerForm;
      const payload: any = {
        username: f.username,
        password: f.password,
        name: f.username, // 使用登录账号作为显示名称
        name_real: f.cardType === '企业' ? f.contactName : f.realName,
        card_type: f.cardType,
        phone: f.cardType === '企业' ? f.contactPhone : f.mobilePhone,
        role: '经纪人', // 固定为经纪人，只有管理员可修改
      };
      if (f.cardType === '企业') {
        payload.company_name = f.companyName;
        payload.bank_account_public = f.bankAccountPublic;
        payload.bank_name = f.bankName;
      } else {
        payload.bank_card = f.bankCard;
        payload.bank_name = f.personalBankName;
        payload.id_card = f.idCard;
      }
      await authApi.register(payload);
      setMode('login');
      setLoginForm({ ...loginForm, username: f.username });
      setError('注册成功，请登录');
    } catch (err: any) {
      setError(err.message || '注册失败');
    } finally {
      setLoading(false);
    }
  };

  const isEnterprise = registerForm.cardType === '企业';

  return (
    <div className="min-h-screen bg-[#0F172A] flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        {/* Logo */}
        <div className="text-center mb-8">
          <img 
            src="/assets/logo.png" 
            alt="余兮目成传媒" 
            className="h-20 mx-auto mb-4 object-contain"
          />
          <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
            经纪人结算管理系统
          </h1>
          <p className="text-[#94A3B8] mt-2 text-sm">高效精准的直播公会财务结算解决方案</p>
        </div>

        <div className="bg-[#1E293B] border border-[#334155] rounded-2xl p-6 shadow-xl">
          {/* 模式切换 */}
          <div className="flex mb-6 bg-[#0F172A] rounded-lg p-1">
            <button onClick={() => { setMode('login'); setError(''); }} className={`flex-1 h-10 rounded-md text-sm font-medium transition-all flex items-center justify-center gap-2 ${mode === 'login' ? 'bg-[#3B82F6] text-white' : 'text-[#94A3B8] hover:text-[#F1F5F9]'}`}>
              <LogIn className="w-4 h-4" /> 登录
            </button>
            <button onClick={() => { setMode('register'); setError(''); }} className={`flex-1 h-10 rounded-md text-sm font-medium transition-all flex items-center justify-center gap-2 ${mode === 'register' ? 'bg-[#3B82F6] text-white' : 'text-[#94A3B8] hover:text-[#F1F5F9]'}`}>
              <UserPlus className="w-4 h-4" /> 注册
            </button>
          </div>

          {error && <div className="mb-4 px-4 py-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">{error}</div>}

          {/* 登录表单 */}
          {mode === 'login' && (
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-sm text-[#94A3B8] mb-2">登录账号</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
                  <input type="text" value={loginForm.username} onChange={e => setLoginForm({ ...loginForm, username: e.target.value })} className="w-full h-10 pl-10 pr-4 bg-[#0F172A] border border-[#334155] rounded-lg text-sm text-[#F1F5F9] placeholder-[#475569] focus:outline-none focus:border-[#3B82F6] transition-colors" placeholder="请输入账号" autoComplete="username" />
                </div>
              </div>
              <div>
                <label className="block text-sm text-[#94A3B8] mb-2">登录密码</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
                  <input type="password" value={loginForm.password} onChange={e => setLoginForm({ ...loginForm, password: e.target.value })} className="w-full h-10 pl-10 pr-4 bg-[#0F172A] border border-[#334155] rounded-lg text-sm text-[#F1F5F9] placeholder-[#475569] focus:outline-none focus:border-[#3B82F6] transition-colors" placeholder="请输入密码" autoComplete="current-password" />
                </div>
              </div>
              <button type="submit" disabled={loading} className="w-full h-11 bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 rounded-lg font-medium transition-all disabled:opacity-50 flex items-center justify-center gap-2">
                <LogIn className="w-4 h-4" />{loading ? '登录中...' : '登录系统'}
              </button>
            </form>
          )}

          {/* 注册表单 */}
          {mode === 'register' && (
            <form onSubmit={handleRegister} className="space-y-4 max-h-[65vh] overflow-y-auto pr-1">
              {/* 类型选择 */}
              <div>
                <label className="block text-sm text-[#94A3B8] mb-2">账户类型 <span className="text-red-400">*</span></label>
                <div className="flex gap-3">
                  {(['个人', '企业'] as CardType[]).map(t => (
                    <button key={t} type="button" onClick={() => setRegisterForm({ ...registerForm, cardType: t })} className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg border transition-all ${registerForm.cardType === t ? 'border-[#3B82F6] bg-[#3B82F6]/10 text-[#3B82F6]' : 'border-[#334155] bg-[#0F172A] text-[#94A3B8] hover:border-[#475569]'}`}>
                      {t === '企业' ? <Building2 className="w-4 h-4" /> : <User className="w-4 h-4" />}{t}
                    </button>
                  ))}
                </div>
              </div>

              {/* 基础信息 */}
              <div>
                <label className="block text-sm text-[#94A3B8] mb-2">登录账号 <span className="text-red-400">*</span></label>
                <input type="text" value={registerForm.username} onChange={e => setRegisterForm({ ...registerForm, username: e.target.value })} className="w-full h-10 px-4 bg-[#0F172A] border border-[#334155] rounded-lg text-sm text-[#F1F5F9] placeholder-[#475569] focus:outline-none focus:border-[#3B82F6] transition-colors" placeholder="设置登录账号" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm text-[#94A3B8] mb-2">登录密码 <span className="text-red-400">*</span></label>
                  <input type="password" value={registerForm.password} onChange={e => setRegisterForm({ ...registerForm, password: e.target.value })} className="w-full h-10 px-4 bg-[#0F172A] border border-[#334155] rounded-lg text-sm text-[#F1F5F9] placeholder-[#475569] focus:outline-none focus:border-[#3B82F6] transition-colors" placeholder="设置密码" />
                </div>
                <div>
                  <label className="block text-sm text-[#94A3B8] mb-2">确认密码 <span className="text-red-400">*</span></label>
                  <input type="password" value={registerForm.confirmPassword} onChange={e => setRegisterForm({ ...registerForm, confirmPassword: e.target.value })} className="w-full h-10 px-4 bg-[#0F172A] border border-[#334155] rounded-lg text-sm text-[#F1F5F9] placeholder-[#475569] focus:outline-none focus:border-[#3B82F6] transition-colors" placeholder="再次输入密码" />
                </div>
              </div>
              {/* 角色固定为经纪人，不可选择 */}

              {/* 企业字段 */}
              {isEnterprise ? (
                <div className="space-y-4 pt-2 border-t border-[#334155]">
                  <h4 className="text-xs font-medium text-amber-400 flex items-center gap-1"><Building2 className="w-3 h-3" /> 企业信息（必填）</h4>
                  <div>
                    <label className="block text-sm text-[#94A3B8] mb-2">企业名称 <span className="text-red-400">*</span></label>
                    <input type="text" value={registerForm.companyName} onChange={e => setRegisterForm({ ...registerForm, companyName: e.target.value })} className="w-full h-10 px-4 bg-[#0F172A] border border-[#334155] rounded-lg text-sm text-[#F1F5F9] placeholder-[#475569] focus:outline-none focus:border-[#F59E0B] transition-colors" placeholder="企业全称" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm text-[#94A3B8] mb-2">对公账户 <span className="text-red-400">*</span></label>
                      <input type="text" value={registerForm.bankAccountPublic} onChange={e => setRegisterForm({ ...registerForm, bankAccountPublic: e.target.value })} className="w-full h-10 px-4 bg-[#0F172A] border border-[#334155] rounded-lg text-sm text-[#F1F5F9] placeholder-[#475569] focus:outline-none focus:border-[#F59E0B] transition-colors" placeholder="对公银行账户" />
                    </div>
                    <div>
                      <label className="block text-sm text-[#94A3B8] mb-2">开户行名称 <span className="text-red-400">*</span></label>
                      <input type="text" value={registerForm.bankName} onChange={e => setRegisterForm({ ...registerForm, bankName: e.target.value })} className="w-full h-10 px-4 bg-[#0F172A] border border-[#334155] rounded-lg text-sm text-[#F1F5F9] placeholder-[#475569] focus:outline-none focus:border-[#F59E0B] transition-colors" placeholder="开户行全称" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm text-[#94A3B8] mb-2">联系人姓名 <span className="text-red-400">*</span></label>
                      <input type="text" value={registerForm.contactName} onChange={e => setRegisterForm({ ...registerForm, contactName: e.target.value })} className="w-full h-10 px-4 bg-[#0F172A] border border-[#334155] rounded-lg text-sm text-[#F1F5F9] placeholder-[#475569] focus:outline-none focus:border-[#F59E0B] transition-colors" placeholder="联系人姓名" />
                    </div>
                    <div>
                      <label className="block text-sm text-[#94A3B8] mb-2">联系电话 <span className="text-red-400">*</span></label>
                      <input type="text" value={registerForm.contactPhone} onChange={e => setRegisterForm({ ...registerForm, contactPhone: e.target.value })} className="w-full h-10 px-4 bg-[#0F172A] border border-[#334155] rounded-lg text-sm text-[#F1F5F9] placeholder-[#475569] focus:outline-none focus:border-[#F59E0B] transition-colors" placeholder="联系电话" />
                    </div>
                  </div>
                </div>
              ) : (
                /* 个人字段 */
                <div className="space-y-4 pt-2 border-t border-[#334155]">
                  <h4 className="text-xs font-medium text-emerald-400 flex items-center gap-1"><User className="w-3 h-3" /> 个人信息（必填）</h4>
                  <div>
                    <label className="block text-sm text-[#94A3B8] mb-2">姓名 <span className="text-red-400">*</span></label>
                    <input type="text" value={registerForm.realName} onChange={e => setRegisterForm({ ...registerForm, realName: e.target.value })} className="w-full h-10 px-4 bg-[#0F172A] border border-[#334155] rounded-lg text-sm text-[#F1F5F9] placeholder-[#475569] focus:outline-none focus:border-[#10B981] transition-colors" placeholder="真实姓名" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm text-[#94A3B8] mb-2">银行卡号 <span className="text-red-400">*</span></label>
                      <input type="text" value={registerForm.bankCard} onChange={e => setRegisterForm({ ...registerForm, bankCard: e.target.value })} className="w-full h-10 px-4 bg-[#0F172A] border border-[#334155] rounded-lg text-sm text-[#F1F5F9] placeholder-[#475569] focus:outline-none focus:border-[#10B981] transition-colors" placeholder="银行卡号" />
                    </div>
                    <div>
                      <label className="block text-sm text-[#94A3B8] mb-2">开户行 <span className="text-red-400">*</span></label>
                      <input type="text" value={registerForm.personalBankName} onChange={e => setRegisterForm({ ...registerForm, personalBankName: e.target.value })} className="w-full h-10 px-4 bg-[#0F172A] border border-[#334155] rounded-lg text-sm text-[#F1F5F9] placeholder-[#475569] focus:outline-none focus:border-[#10B981] transition-colors" placeholder="开户行名称" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm text-[#94A3B8] mb-2">手机号码 <span className="text-red-400">*</span></label>
                      <input type="text" value={registerForm.mobilePhone} onChange={e => setRegisterForm({ ...registerForm, mobilePhone: e.target.value })} className="w-full h-10 px-4 bg-[#0F172A] border border-[#334155] rounded-lg text-sm text-[#F1F5F9] placeholder-[#475569] focus:outline-none focus:border-[#10B981] transition-colors" placeholder="手机号" />
                    </div>
                    <div>
                      <label className="block text-sm text-[#94A3B8] mb-2">身份证号 <span className="text-red-400">*</span></label>
                      <input type="text" value={registerForm.idCard} onChange={e => setRegisterForm({ ...registerForm, idCard: e.target.value })} className="w-full h-10 px-4 bg-[#0F172A] border border-[#334155] rounded-lg text-sm text-[#F1F5F9] placeholder-[#475569] focus:outline-none focus:border-[#10B981] transition-colors" placeholder="身份证号码" />
                    </div>
                  </div>
                </div>
              )}

              <button type="submit" disabled={loading} className={`w-full h-11 rounded-lg font-medium transition-all disabled:opacity-50 flex items-center justify-center gap-2 ${isEnterprise ? 'bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600' : 'bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600'}`}>
                <UserPlus className="w-4 h-4" />{loading ? '注册中...' : `注册${isEnterprise ? '企业' : '个人'}账号`}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
